import React, { PropsWithChildren, useMemo, memo } from "react";
import { FilterInput as FilterInputType } from "./types";
import { RiseStar } from "@/assets/svg/RiseStar";
import {
  DateRangeInput,
  DropdownMultiSelect,
  PillSelect,
  PillSingleSelect,
  TokenizedTextInput,
} from "@/components/FormInputs";
import { useController } from "react-hook-form";
import { cn } from "@/lib/utils";

export const FilterInput = memo(function FilterInput({
  filter,
}: {
  filter: FilterInputType;
}) {
  const { field } = useController({
    name: filter.name,
    rules: { required: false },
  });

  // generate an id for the label based on the input name
  const labelId = useMemo(
    () =>
      filter.name.replace(/[^a-zA-Z0-9-_]/g, "") +
      "-" +
      Math.floor(Math.random() * 0xfffff * 1000).toString(16),
    [filter.name],
  );

  switch (filter.type) {
    case "DateRange":
      return (
        <FilterInputWrapper filter={filter} labelId={labelId}>
          <DateRangeInput
            value={field.value}
            onUpdate={field.onChange}
            placeholder={filter.placeholder}
            labeledBy={labelId}
          />
        </FilterInputWrapper>
      );
      break;
    case "Checkboxes":
      if (filter.options) {
        return (
          <FilterInputWrapper
            filter={filter}
            labelClassName={"mb-2"}
            labelId={labelId}
          >
            <PillSelect
              allowMultiple={true}
              options={filter.options}
              value={field.value}
              onUpdate={field.onChange}
              labeledBy={labelId}
            />
          </FilterInputWrapper>
        );
      }
      break;
    case "Radio":
      if (filter.options) {
        return (
          <FilterInputWrapper
            filter={filter}
            labelId={labelId}
            labelClassName={"mb-2"}
          >
            <PillSingleSelect
              options={filter.options}
              value={field.value}
              onUpdate={field.onChange}
              labeledBy={labelId}
            />
          </FilterInputWrapper>
        );
      }
      break;
    case "DropdownMultiSelect":
      if (filter.options) {
        return (
          <FilterInputWrapper filter={filter} labelId={labelId}>
            <DropdownMultiSelect
              options={filter.options}
              value={field.value}
              onUpdate={field.onChange}
              placeholder={filter.placeholder}
              allowSearch={!filter.disableSearch}
            />
          </FilterInputWrapper>
        );
      }
      break;
    case "TokenizedTextInput":
      return (
        <FilterInputWrapper filter={filter} labelId={labelId}>
          <TokenizedTextInput
            value={field.value}
            onUpdate={field.onChange}
            labeledBy={labelId}
            placeholder={filter.placeholder}
            tokenIsValid={filter.tokenIsValid}
          />
        </FilterInputWrapper>
      );
      break;
  }
  return <></>;
});

const FilterInputWrapper = ({
  filter,
  labelClassName,
  children,
  labelId,
}: PropsWithChildren<{
  filter: FilterInputType;
  labelId: string;
  labelClassName?: string;
}>) => {
  return (
    <div className="mb-4" data-testid="filter-input">
      <div className={cn("mb-1 py-1", labelClassName)}>
        <label id={labelId} className="flex leading-5 font-bold">
          {filter.label}
          {filter.filterDisplayType === "RISE" ? (
            <RiseStar className={"mt-.5 ml-2"} />
          ) : undefined}
        </label>
      </div>
      {children}
    </div>
  );
};
